import { getErrorMessageOrDefault } from "@vortex/shared";
import * as React from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import Modal from "../../../controls/Modal";
import { log } from "../../../util/log";
import opn from "../../../util/opn";
import { Campaign, Content, nexusModsURL, Section } from "../../../util/util";
import { MainContext } from "../../../views/MainWindow";
import { NEXUS_BASE_URL, PREMIUM_PATH } from "../constants";
import NXMUrl from "../NXMUrl";
import { nexusGamesProm } from "../util";
import { makeFileUID } from "../util/UIDs";
import NewFreeDownloadModal from "./NewFreeDownloadModal";
const FILE_QUERY = {
    modId: true,
    mod: {
        summary: true,
        name: true,
        pictureUrl: true,
        uploader: {
            name: true,
            avatar: true,
        },
    },
    game: {
        domainName: true,
    },
    name: true,
    owner: {
        memberId: true,
    },
};
const makeUnknown = (t, url) => {
    return {
        modId: undefined,
        mod: {
            summary: t("N/A"),
            name: t("Mod with id {{modId}}", { modId: url.modId }),
            pictureUrl: null,
            uploader: {
                name: t("N/A"),
                avatar: undefined,
            },
        },
        game: {
            domainName: undefined,
        },
        name: t("N/A"),
        owner: {
            member_id: undefined,
        },
    };
};
function nop() {
    // nop
}
function FreeUserDLDialog(props) {
    const { t, nexus, onCancel, onDownload, onSkip, onUpdated, onRetry, onCheckStatus, } = props;
    const urls = useSelector((state) => state.session["nexus"].freeUserDLQueue);
    const userInfo = useSelector((state) => state.persistent["nexus"].userInfo);
    const collectionInstallSession = useSelector((state) => state.session?.["collections"]?.activeSession);
    const context = React.useContext(MainContext);
    const [fileInfo, setFileInfo] = React.useState(null);
    const [campaign, setCampaign] = React.useState(undefined);
    const [positionText, setPositionText] = React.useState("1/1");
    const lastFetchUrl = React.useRef();
    const show = urls.length > 0 && !userInfo?.isPremium;
    React.useEffect(() => {
        if (!show)
            return;
        const handleFocus = () => {
            console.log("Window gained focus. Modal is open");
            checkStatus();
        };
        window.addEventListener("focus", handleFocus);
        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [show]);
    React.useEffect(() => {
        if (collectionInstallSession?.downloadedCount != null) {
            const position = collectionInstallSession.downloadedCount + 1;
            const total = Math.max(collectionInstallSession.totalRequired +
                collectionInstallSession.totalOptional, 1);
            setPositionText(`${position}/${total}`);
        }
    }, [collectionInstallSession]);
    React.useEffect(() => {
        // if userInfo is updated, and isPremium is true, then retry
        if (userInfo !== undefined)
            if (userInfo?.isPremium && urls.length > 0)
                retry();
    }, [userInfo]);
    React.useEffect(() => {
        const fetchFileInfo = async () => {
            const url = new NXMUrl(urls[0]);
            onUpdated();
            setFileInfo(null);
            setCampaign(url.getParam("campaign"));
            try {
                // makeFileUID needs the nexus games list to map domain -> numeric game id.
                // This should've been done on startup, but there appears to be
                // a race condition https://github.com/Nexus-Mods/Vortex/issues/22466
                await nexusGamesProm();
                const fileInfoList = await nexus.modFilesByUid(FILE_QUERY, [
                    makeFileUID({ fileId: url.fileId.toString(), gameId: url.gameId }),
                ]);
                if (fileInfoList.length > 0) {
                    setFileInfo(fileInfoList[0]);
                }
                else {
                    setFileInfo(makeUnknown(t, url));
                }
            }
            catch (err) {
                log("error", "failed to fetch file information", {
                    url,
                    error: getErrorMessageOrDefault(err),
                });
                setFileInfo(makeUnknown(t, url));
            }
        };
        if (urls.length > 0 && urls[0] !== lastFetchUrl.current) {
            lastFetchUrl.current = urls[0];
            fetchFileInfo();
        }
    }, [urls]);
    const checkStatus = React.useCallback(() => {
        onCheckStatus();
    }, [onCheckStatus]);
    const retry = React.useCallback(() => {
        onRetry(urls[0]);
    }, [onRetry, urls]);
    const cancel = React.useCallback(() => {
        onCancel(urls[0]);
    }, [onCancel, urls]);
    const download = React.useCallback(() => {
        onDownload(urls[0]);
    }, [onDownload, urls]);
    const skip = React.useCallback(() => {
        onSkip(urls[0]);
    }, [onSkip, urls]);
    const openModPage = React.useCallback(() => {
        opn(`${NEXUS_BASE_URL}/${fileInfo.game.domainName}/mods/${fileInfo.modId}`);
    }, [fileInfo]);
    const openAuthorPage = React.useCallback(() => {
        opn(`${NEXUS_BASE_URL}/${fileInfo.game.domainName}/users/${fileInfo.owner.memberId}`);
    }, [fileInfo]);
    const goPremium = React.useCallback(() => {
        context.api.events.emit("analytics-track-click-event", "Go Premium", "Download Mod");
        opn(nexusModsURL(PREMIUM_PATH, {
            section: Section.Users,
            campaign: Campaign.BuyPremium,
            content: Content.DownloadModModal,
        })).catch(() => null);
    }, [campaign]);
    return (React.createElement(Modal, { id: "free-user-dl-dialog", show: show, onHide: nop },
        React.createElement(Modal.Header, null,
            React.createElement(Modal.Title, null, t("Download mod"))),
        React.createElement(Modal.Body, null,
            React.createElement(NewFreeDownloadModal, { fileInfo: fileInfo, goPremium: goPremium, openModPage: openModPage, positionText: positionText, t: t, onDownload: download })),
        React.createElement(Modal.Footer, null,
            React.createElement(Button, { id: "cancel-button", onClick: cancel }, t("Cancel install")),
            React.createElement(Button, { id: "skip-button", onClick: skip }, t("Skip mod")))));
}
export default FreeUserDLDialog;
